// ABOUTME: Request-phase measurement extracted from the apiDirectCall transport path.
// ABOUTME: Correlates browser telemetry with ingress logs via a per-request UUID.
import {
    captureNetworkFailureDiagnostic,
    captureSlowApiDiagnostic,
    isKnownNextHopProtocol,
    NetworkFailureReason,
    ResourceTimingUnavailableReason
} from "observability/performanceLogger";

// The ingress logs the full request URI, so the correlation ID rides the query
// string instead of a header. External URLs never pass through here.
export const withIngressCorrelation = (
    rawUrl: string
): { url: string; requestId?: string } => {
    if (!globalThis.crypto?.randomUUID) {
        return { url: rawUrl };
    }
    try {
        const url = new URL(rawUrl);
        const requestId = globalThis.crypto.randomUUID();
        url.searchParams.set("compleoRequestId", requestId);
        return { url: url.toString(), requestId };
    } catch {
        return { url: rawUrl };
    }
};

const pendingRequestIds = new Set<string>();
const observedResources = new Map<string, PerformanceResourceTiming>();
let resourceObserver: PerformanceObserver | undefined;

const requestIdFromResourceName = (name: string): string | undefined => {
    if (!name.includes("compleoRequestId=")) return undefined;
    try {
        return new URL(name).searchParams.get("compleoRequestId") || undefined;
    } catch {
        // Other resource URLs cannot affect request diagnostics.
        return undefined;
    }
};

const rememberResourceEntries = (entries: PerformanceEntry[]) => {
    for (const entry of entries) {
        const requestId = requestIdFromResourceName(entry.name);
        if (requestId && pendingRequestIds.has(requestId)) {
            observedResources.set(
                requestId,
                entry as PerformanceResourceTiming
            );
        }
    }
};

export const observeRequestResourceTiming = (requestId: string) => {
    if (typeof PerformanceObserver === "undefined") return;
    try {
        if (!resourceObserver) {
            resourceObserver = new PerformanceObserver((list) =>
                rememberResourceEntries(list.getEntries())
            );
            resourceObserver.observe({ type: "resource" });
        }
        pendingRequestIds.add(requestId);
    } catch {
        try {
            resourceObserver?.disconnect();
        } catch {
            // Diagnostics must not affect the request outcome.
        }
        resourceObserver = undefined;
    }
};

const takeObservedRecords = () => {
    try {
        if (resourceObserver) {
            rememberResourceEntries(resourceObserver.takeRecords());
        }
    } catch {
        // The browser's performance timeline remains a fallback.
    }
};

const releaseRequestResourceTiming = (requestId: string) => {
    pendingRequestIds.delete(requestId);
    observedResources.delete(requestId);
    if (pendingRequestIds.size === 0) {
        try {
            resourceObserver?.disconnect();
        } catch {
            // Diagnostics must not affect the request outcome.
        }
        resourceObserver = undefined;
    }
};

// The lookup owns the whole "why detailed timing is missing" taxonomy: it
// resolves a resource with usable response milestones, or the reason none
// could be used. "no_request_id" is synthesized by the reporting layer when
// the call never carried a correlation id; the lookup itself never returns it.
type ResourceTimingLookup =
    | { resource: PerformanceResourceTiming; reason?: undefined }
    | {
          resource?: PerformanceResourceTiming;
          reason: ResourceTimingUnavailableReason;
      };

// Zeroed request/response milestones mean either the browser hid them (e.g.
// missing Timing-Allow-Origin) or the request failed before any response
// arrived — the expected shape for an api_no_response event with usable
// connection phases. Other violations of the expected ordering are invalid.
const classifyResourceTiming = (
    resource: PerformanceResourceTiming
): ResourceTimingLookup =>
    resource.requestStart > 0 &&
    resource.responseStart >= resource.requestStart &&
    resource.responseEnd >= resource.responseStart
        ? { resource }
        : {
              resource,
              reason:
                  resource.requestStart === 0 || resource.responseStart === 0
                      ? "milestones_unavailable"
                      : "invalid_milestones"
          };

// Resource Timing entries are published asynchronously, so a bounded retry
// ladder (0/100/150 ms) gives the browser a chance to record the entry before
// we give up. The waits only delay the async telemetry, never the API result.
export const findRequestResourceTiming = async (
    requestId: string
): Promise<ResourceTimingLookup> => {
    let lookupFailed = false;
    for (const delayMs of [0, 100, 150]) {
        if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        takeObservedRecords();
        const observed = observedResources.get(requestId);
        if (observed) return classifyResourceTiming(observed);
        try {
            const resources = performance.getEntriesByType(
                "resource"
            ) as PerformanceResourceTiming[];
            for (let index = resources.length - 1; index >= 0; index--) {
                if (
                    requestIdFromResourceName(resources[index].name) ===
                    requestId
                ) {
                    return classifyResourceTiming(resources[index]);
                }
            }
        } catch {
            lookupFailed = true;
        }
    }
    return {
        reason: lookupFailed ? "lookup_failed" : "entry_not_found"
    };
};

type ApiRequestTimingStamps = {
    routePath: string;
    endpoint: string;
    verb: string;
    fullUrl: boolean;
    statusCode?: number;
    errorMessage?: string;
    networkFailureReason?: NetworkFailureReason;
    requestId?: string;
    startedAt: number;
    completedAt: number;
    authDurationMs: number;
    transportStartedAt: number;
    transportFinishedAt: number;
    transportStartedAtEpochMs: number;
};

const safeDuration = (start: number, end: number) =>
    Number.isFinite(start) &&
    Number.isFinite(end) &&
    start > 0 &&
    end >= start &&
    end - start <= 300_000
        ? end - start
        : undefined;

// Fire-and-forget: derives the request phases from the collected stamps and
// forwards them to the slow-API diagnostic sink. Must never affect the request
// outcome, so every failure is swallowed.
export const reportApiRequestTiming = async (
    stamps: ApiRequestTimingStamps
) => {
    const clientFinishedAt = new Date().toISOString();
    try {
        const timing: ResourceTimingLookup = stamps.requestId
            ? await findRequestResourceTiming(stamps.requestId)
            : { reason: "no_request_id" };
        const resource = timing.resource;
        const hasResponseTiming = !!resource && !timing.reason;
        const hasBrowserQueueTiming =
            !!resource &&
            resource.fetchStart > 0 &&
            resource.requestStart >= resource.fetchStart;
        const toEpochMs = (relativeMs: number) =>
            Number.isFinite(performance.timeOrigin)
                ? Math.round(performance.timeOrigin + relativeMs)
                : undefined;
        const captureArgs = {
            routePath: stamps.routePath,
            endpoint: stamps.endpoint,
            verb: stamps.verb,
            durationMs: stamps.completedAt - stamps.startedAt,
            statusCode: stamps.statusCode,
            fullUrl: stamps.fullUrl,
            errorMessage: stamps.errorMessage,
            networkFailureReason: stamps.networkFailureReason,
            requestId: stamps.requestId,
            clientFinishedAt,
            transportStartedAtEpochMs: stamps.transportStartedAtEpochMs,
            authDurationMs: stamps.authDurationMs,
            transportDurationMs:
                stamps.transportFinishedAt - stamps.transportStartedAt,
            processingDurationMs:
                stamps.completedAt - stamps.transportFinishedAt,
            browserPostResponseDurationMs:
                hasResponseTiming && stamps.completedAt >= resource.responseEnd
                    ? stamps.completedAt - resource.responseEnd
                    : undefined,
            browserRequestStartedAtEpochMs:
                resource && resource.requestStart > 0
                    ? toEpochMs(resource.requestStart)
                    : undefined,
            browserResponseEndedAtEpochMs: hasResponseTiming
                ? toEpochMs(resource.responseEnd)
                : undefined,
            browserQueueDurationMs: hasBrowserQueueTiming
                ? resource.requestStart - resource.fetchStart
                : undefined,
            responseWaitDurationMs: hasResponseTiming
                ? resource.responseStart - resource.requestStart
                : undefined,
            responseTransferDurationMs: hasResponseTiming
                ? resource.responseEnd - resource.responseStart
                : undefined,
            dnsDurationMs: resource
                ? safeDuration(
                      resource.domainLookupStart,
                      resource.domainLookupEnd
                  )
                : undefined,
            connectDurationMs: resource
                ? safeDuration(resource.connectStart, resource.connectEnd)
                : undefined,
            tlsDurationMs: resource
                ? safeDuration(
                      resource.secureConnectionStart,
                      resource.connectEnd
                  )
                : undefined,
            nextHopProtocol:
                resource && isKnownNextHopProtocol(resource.nextHopProtocol)
                    ? resource.nextHopProtocol
                    : undefined,
            resourceTimingAvailable: hasResponseTiming,
            resourceTimingUnavailableReason: timing.reason
        };
        if (stamps.networkFailureReason) {
            await captureNetworkFailureDiagnostic(captureArgs);
        } else {
            await captureSlowApiDiagnostic(captureArgs);
        }
    } catch {
        // Diagnostics must never change the request outcome.
    } finally {
        if (stamps.requestId) releaseRequestResourceTiming(stamps.requestId);
    }
};
